import { AttachmentBadge } from '../../../shared/ui/feedback/AttachmentBadge';
import { TechnicianFeedbackBadge } from '../../../shared/ui/feedback/TechnicianFeedbackBadge';
import type { BlockSegment } from '../model/boardTypes';

/**
 * Below this many pixels a segment drops its Gewerk and order number — four truncated lines in a
 * sliver read as noise. Wider than `HOUR_WIDTH / 2`, so a 30-minute block keeps its detail.
 */
const DETAIL_MIN_WIDTH = 72;

/** Below this, only the time survives. */
const LABEL_MIN_WIDTH = 40;

/** Below this the segment is a bare colour strip; the block's tooltip still carries everything. */
const TEXT_MIN_WIDTH = 22;

/**
 * How far the strips a hovered resize handle will *not* touch recede.
 *
 * Milder than the drag ghost's 0.4: this fires on hover, on a block still carrying its real text.
 */
const DIMMED_SEGMENT_OPACITY = 0.55;

interface Props {
  segment: BlockSegment;
  /** Share of the block's width, so the density rule can be applied without measuring the DOM. */
  widthPx: number;
  /** A resize handle is hovered and it is not this strip's — recede so the target stands out. */
  dimmed: boolean;
}

/** One coloured strip of a block — Anfahrt, Arbeit or Abfahrt — with as much text as it can carry. */
export const SegmentCell = ({ segment, widthPx, dimmed }: Props) => {
  if (segment.minutes <= 0) return null;

  const showText = widthPx >= TEXT_MIN_WIDTH;
  const showLabel = widthPx >= LABEL_MIN_WIDTH;
  const showDetail = widthPx >= DETAIL_MIN_WIDTH;

  const subLabel = showDetail ? segment.subLabel : undefined;
  const badge = showDetail ? segment.badge : undefined;
  const hasBottom = Boolean(subLabel || badge);

  return (
    <div
      className="relative flex h-full min-w-0 flex-col overflow-hidden rounded-sm px-1.5 py-1 font-medium leading-tight text-white transition-opacity"
      style={{
        flex: segment.minutes,
        backgroundColor: segment.color,
        minWidth: 4,
        justifyContent: hasBottom ? 'space-between' : 'center',
        opacity: dimmed ? DIMMED_SEGMENT_OPACITY : 1,
      }}
    >
      {showText && (
        <div className="flex flex-col gap-px overflow-hidden">
          {(segment.inProgress || segment.needsRescheduling || segment.topLabel) && (
            <div className="flex items-center gap-1 overflow-hidden">
              {segment.needsRescheduling && (
                // Amber, matching the `Neu planen` pill on the Zeitmanagement list. Like `Läuft` it
                // keeps only its dot below `DETAIL_MIN_WIDTH`; in a sliver the hatched fill and the
                // dashed outline carry the state instead. `z-10` lifts it above that hatch — the
                // pill is the one part of a ghosted block that has to stay crisp.
                <span className="relative z-10 inline-flex shrink-0 items-center gap-1 rounded bg-amber-500 px-1 py-px text-[8px] font-bold uppercase leading-none text-white">
                  <span className="h-1 w-1 shrink-0 rounded-full bg-white" />
                  {showDetail && <span>Umdisponieren</span>}
                </span>
              )}
              {segment.inProgress && (
                <span className="inline-flex shrink-0 items-center gap-1 rounded bg-red-500 px-1 py-px text-[8px] font-bold uppercase leading-none text-white">
                  <span className="h-1 w-1 shrink-0 animate-pulse rounded-full bg-white" />
                  {showDetail && <span>Läuft</span>}
                </span>
              )}
              {segment.topLabel && (
                <span className="truncate text-[11px] font-semibold">{segment.topLabel}</span>
              )}
            </div>
          )}
          {showLabel && segment.label && (
            <span className={`truncate text-xs ${segment.topLabel ? '' : 'leading-snug'}`}>
              {segment.label}
            </span>
          )}
        </div>
      )}

      {hasBottom && (
        <div className="flex flex-col gap-px overflow-hidden">
          {subLabel && <span className="truncate text-[10px] opacity-80">{subLabel}</span>}
          {badge && (
            <span className="truncate text-[9px] leading-none tabular-nums opacity-75">
              {badge}
            </span>
          )}
        </div>
      )}

      {/* One corner, both pills: the office's paperclip and the worker's hard hat. Inline variants
          inside a single absolute wrapper, so they sit side by side instead of on top of each other. */}
      <div className="pointer-events-none absolute right-0.5 top-0.5 z-10 flex items-center gap-0.5">
        <AttachmentBadge count={segment.attachmentCount ?? 0} inline />
        <TechnicianFeedbackBadge
          hasComment={segment.technicianFeedback?.hasComment ?? false}
          attachmentCount={segment.technicianFeedback?.attachmentCount ?? 0}
        />
      </div>

      {segment.inProgress && (
        <div
          className="pointer-events-none absolute inset-0 rounded-sm ring-2 ring-inset ring-white animate-pulse"
          aria-hidden
        />
      )}
    </div>
  );
};
